<script lang="ts" setup>
import AccentMarker from '../../AccentMarker.vue';
import DefinitionListSection from '../../DefinitionListSection.vue';
import WhereToConsultCard from '../../WhereToConsult/WhereToConsultCard.vue';

defineProps<{
  imageNumber: number;
}>();
</script>

<template>
  <WhereToConsultCard title="iDeCoとは" :image-number="imageNumber">
    <p>
      iDeCo（個人型確定拠出年金）は、ご自身が拠出した掛金で運用を行い、老後資金の準備に取り組むための制度です。
    </p>
    <p>
      掛金や運用で得られた利益を60歳以降に老齢給付金として受け取ることができ、国内在住で一定の加入条件を満たす20歳以上65歳未満のすべての方が加入できます。
    </p>
    <p>
      原則として60歳になるまで一切の資産を引き出すことはできませんが、iDeCoに拠出した掛金は全額が所得控除の対象となり、運用益は全額が非課税で、資産を引き出す際にも大きな所得控除の対象となる点が特徴です。
    </p>
    <p>
      日本は世界的に見ても平均寿命の延伸化が進む長寿国とされています。厚生労働省の「
      <NuxtLink
        href="https://www.mhlw.go.jp/toukei/saikin/hw/life/life23/index.html"
        external
        target="_blank"
      >
        簡易生命表の概況</NuxtLink
      >」によると、令和5年時点の平均寿命は<AccentMarker>男性が81.09歳、女性は87.14歳</AccentMarker>となっており、65歳の定年退職後も20年以上の生活が続きます。
    </p>
    <p>
      「人生100年時代」と呼ばれる昨今、今後も日本人の平均寿命は延伸化が進むとされており、長期化する老後を見据えた事前準備が必要です。
    </p>
    <p>
      iDeCoは老後資金の貯蓄に取り組みながら、現在の生活における税制上の優遇措置が講じられている制度です。公的年金だけでは不足しがちな老後資金を補填するためにも、積極的なiDeCoの活用を検討してみましょう。
    </p>
    <h3>iDeCoのメリット</h3>
    <p>
      資産形成の方法として「iDeCo」を選択するメリットは、次の5つが挙げられます。
    </p>
    <DefinitionListSection>
      <dt>iDeCoのメリット</dt>
      <dd>
        <ul>
          <li>iDeCoに拠出した掛金は全額が所得控除の対象になる</li>
          <li>iDeCoの運用で得られた利益は全額が非課税で再投資に回せる</li>
          <li>iDeCoから受け取る給付金は大きな所得控除の対象になる</li>
          <li>
            退職や転職時もiDeCoの運用を継続または他制度への資産の持ち運びができる
          </li>
          <li>
            銀行口座からの自動引き落としで無理なく自然と先取り貯蓄に取り組める
          </li>
        </ul>
      </dd>
    </DefinitionListSection>
    <p>
      日本の税制では、毎年1月1日〜12月31日までに得た所得から各種控除を差し引いた「課税所得」に対し、所定の税率を乗じて所得税や住民税が計算されます。
    </p>
    <p>
      iDeCoに拠出した掛金は全額が所得控除の対象となるため、所得税や住民税の計算時に用いられる課税所得からiDeCoへの拠出額を差し引き、その結果として税負担の軽減を期待できる点が特徴です。
    </p>
    <p>
      また、通常の投資で得た利益には20.315％の税金が課せられますが、iDeCoの運用で得られた利益は全額が非課税となるため、より多くの資産を効率良く再投資に回せます。
      ※2024年8月31日時点
    </p>
    <p>
      iDeCoの老齢給付金は「年金形式」、「一時金形式」、「年金＋一時金形式」の3パターンから受取方法を選択でき、どのパターンも所得控除の対象となるため、受取時にも大きな節税効果を期待できます。
    </p>
    <p>
      銀行口座からの自動引き落としで掛金を拠出する仕組みのため、手元のお金を使い込んでしまう方も、自然と先取り貯蓄に取り組める点も魅力のひとつです。
    </p>
    <h3>iDeCoのデメリット</h3>
    <p>
      老後資金を準備する手段としてメリットの多い「iDeCo」ですが、一方でいくつかのデメリットがある点に注意が必要です。
    </p>
    <DefinitionListSection>
      <dt>iDeCoのデメリット</dt>
      <dd>
        <ul>
          <li>
            原則として60歳になるまで一切の資産を引き出せず途中解約ができない
          </li>
          <li>加入期間が10年未満の場合は受給可能年齢が繰り下げられる</li>
          <li>
            運用成績によって受け取れる給付金額が異なり、元本保証や利回り保証がない
          </li>
          <li>iDeCoを利用するには国民年金の被保険者資格に該当する必要がある</li>
          <li>
            加入資格によってiDeCoに拠出可能な掛金の上限額が異なる（上限月額6.8万円）
          </li>
          <li>iDeCo専用口座の開設と維持には手数料が発生する</li>
        </ul>
      </dd>
    </DefinitionListSection>
    <p>
      iDeCoは老後資金の準備を目的とした確定拠出年金制度で、原則として60歳になるまで一切の資産を引き出せず、途中解約もできません。
    </p>
    <p>
      特別な要件を満たす場合には「脱退一時金」の受給が可能ですが、60歳未満で国民年金保険料の免除者であることなど、特殊な事情がない限りiDeCoの脱退が認められていない点には注意が必要です。
    </p>
    <p>
      また、iDeCoはご自身で掛金を拠出し、自分で運用を行うことで老後に給付金を受け取る仕組みです。運用商品に元本確保商品がある場合もありますが、iDeCo自体には元本保証や利回り保証は存在せず、老齢給付金の受給額も運用成績によって変動する点にご注意ください。
    </p>
    <p>
      iDeCoを利用できるのは、国民年金の被保険者資格に該当する方に限られています。
    </p>
    <p>
      第1号被保険者（自営業者やフリーランスなど）、第2号被保険者（会社員など）、第3号被保険者（厚生年金の被保険者に扶養される配偶者など）などの加入区分によって、拠出可能な上限額が異なる点を覚えておきましょう。
    </p>
  </WhereToConsultCard>
</template>

<style lang="scss" module></style>
